/**
 * Investigation logs for Quick Response → preview → Submit to Response.
 * Uses `log()` so lines appear in the dev-server terminal without enabling Debug panel.
 */

import { log, logWarn } from './logging';
import { prepareBidiTextForWordInsert } from './bidiText';

const PREFIX = '[QuickResponse]';

export function getQuickResponseRuntimeContext(): Record<string, unknown> {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  let officePlatform: string | undefined;
  try {
    if (typeof Office !== 'undefined' && Office.context?.platform != null) {
      officePlatform = String(Office.context.platform);
    }
  } catch {
    officePlatform = undefined;
  }
  const platform = nav?.platform ?? '';
  const userAgent = nav?.userAgent ?? '';
  const isMac =
    /Mac/i.test(platform) ||
    /Macintosh/i.test(userAgent) ||
    officePlatform === 'Mac';
  return {
    userAgent,
    platform,
    language: nav?.language,
    officePlatform,
    isMac,
  };
}

/** Short text snapshot for logs — shows brackets explicitly. */
export function qrTextSnapshot(label: string, text: string | undefined | null): Record<string, unknown> {
  const value = text ?? '';
  const tail = value.length > 120 ? value.slice(-120) : value;
  return {
    label,
    len: value.length,
    tail,
    hasOpenBracket: value.includes('['),
    hasCloseBracket: value.includes(']'),
    endsWithSuffix:
      value.trimEnd().endsWith('[ראה תיקון מוצע]') ||
      value.trimEnd().endsWith('[לא מקובל]') ||
      value.trimEnd().endsWith('[מקובל]') ||
      value.trimEnd().endsWith('[דורש אישור חברה]') ||
      value.trimEnd().endsWith(']ראה תיקון מוצע[') ||
      value.trimEnd().endsWith(']לא מקובל[') ||
      value.trimEnd().endsWith(']מקובל[') ||
      value.trimEnd().endsWith(']דורש אישור חברה['),
  };
}

export function logQuickResponseStage(
  stage: string,
  data: Record<string, unknown>,
): void {
  log(`${PREFIX} ${stage}`, {
    ...getQuickResponseRuntimeContext(),
    ...data,
  });
  try {
    if (typeof window !== 'undefined') {
      const prev = (window as any).__mccLastQrStages;
      const next = Array.isArray(prev) ? prev.slice(-40) : [];
      next.push({ stage, at: Date.now(), ...data });
      (window as any).__mccLastQrStages = next;
      (window as any).__mccLastQrStage = { stage, at: Date.now(), ...data };
    }
  } catch {
    // ignore
  }
}

/** Bump when the profile schema changes so webpack remount is visible in logs. */
export const APPLY_PROFILE_REV = 'apply-timing-v2';

export function createQrProfiler(label: string) {
  const startedAt = Date.now();
  let lastAt = startedAt;
  return {
    startedAt,
    mark(stage: string, data: Record<string, unknown> = {}): void {
      const now = Date.now();
      const stepMs = now - lastAt;
      const totalMs = now - startedAt;
      lastAt = now;
      logQuickResponseStage(`profile: ${label} / ${stage}`, {
        profileRev: APPLY_PROFILE_REV,
        stepMs,
        totalMs,
        ...data,
      });
    },
  };
}

export function logQuickResponseBidiCompare(
  stage: string,
  logicalText: string,
): void {
  const prepared = prepareBidiTextForWordInsert(logicalText);
  if (prepared === logicalText) {
    logQuickResponseStage(`${stage}: bidi no-op`, qrTextSnapshot('logical', logicalText));
    return;
  }
  logQuickResponseStage(`${stage}: bidi pre-swap`, {
    logical: qrTextSnapshot('logical', logicalText),
    prepared: qrTextSnapshot('prepared', prepared),
  });
}

export function logQuickResponseMismatchWarning(
  stage: string,
  baseline: string,
  target: string,
): void {
  const baseNorm = baseline.replace(/\s+/g, ' ').trim();
  const targetNorm = target.replace(/\s+/g, ' ').trim();
  if (!baseNorm || targetNorm.startsWith(baseNorm)) return;
  logWarn(`${PREFIX} ${stage}: target does not extend baseline as append`, {
    baseline: qrTextSnapshot('baseline', baseline),
    target: qrTextSnapshot('target', target),
    ...getQuickResponseRuntimeContext(),
  });
}
